import React, { useState, useEffect } from 'react';
import { useSelector } from 'react-redux';
import moment from 'moment';

import BootstrapTable from 'react-bootstrap-table-next';
import paginationFactory from 'react-bootstrap-table2-paginator';

import { InsightsOffCanvas } from '../../components/InsightsOffCanvas';
import { DataMapperInsightArchiveTable } from '../DataMapper/DataMapperInsightArchiveTable';
import { useUiStore } from '../../hooks/useUiStore';

import './InsightArchiveTable.css';

export const InsightArchiveTable = (_data) => {
  // Custom Hook for InsightOffCanvas
  const { openInsightDetails, closeInsightDetails } = useUiStore();
  // Close if one OffCanvas is open
  useEffect(() => {
    closeInsightDetails();
  }, []);

  // all insights from store
  const { insights, lastUpdatedInsight, lastAction } = useSelector(
    (state) => state.insights,
  );
  const { selectedTenancy } = useSelector((state) => state.clinicianProfile);

  const insightArchiveColumn =
    selectedTenancy?.features?.insightsConfig?.insightArchiveColumn || {};

  const { showMentalIlLStatus = true, showSupport = true } =
    insightArchiveColumn;

  // State for switch (show/hide ignored status in insight archive)
  const [isChecked, setIsChecked] = useState(false);

  // Type of Action Status
  const actionStatus = {
    E: 'Closed',
    A: 'Closed',
    D: 'Closed',
    C: 'Closed',
    null: 'Review in progress',
    I: 'Ignored',
  };

  // Action Log Format inside Table
  function notesFormatter(cell) {
    let uKey = 0;
    if (cell) {
      if (cell.length > 0) {
        // Sort Actions by Date
        let actions = cell.slice().sort((a, b) => {
          let c = moment(new Date(a.when_created));
          let d = moment(new Date(b.when_created));
          return d - c;
        });

        return actions.map(
          (e) =>
            uKey < 5 && (
              <li key={uKey++}>
                {actionStatus[e.action] ? actionStatus[e.action] : ''}
                {e.conditional_answer ? ', ' + e.conditional_answer : ''}
                {e.reason ? ', ' + e.reason : ''} -{' '}
                {e.created_by.first_name + ' ' + e.created_by.last_name} -{' '}
                {moment(e.when_created).format('DD MMM YY')}
              </li>
            ),
        );
      } else {
        return <p></p>;
      }
    } else {
      return <p>No insights</p>;
    }
  }

  // Link Format inside Table
  const linkFormatter = (cell, row, rowIndex, formatExtraData) => {
    if (cell) {
      return (
        <>
          {formatExtraData === 'Add action' && (
            <a
              href="#"
              onClick={() => {
                openInsightDetails(
                  insights.find((insight) => insight.id === row.id),
                );
              }}
            >
              {formatExtraData}
            </a>
          )}

          {formatExtraData === 'View' && <a href="">{formatExtraData}</a>}
        </>
      );
    }
  };

  // Table Columns
  const columns = [
    {
      dataField: 'id',
      text: 'Insight Id',
      classes: 'col8',
      hidden: true,
    },
    {
      dataField: 'insightDateRisk',
      text: 'Insight',
      classes: 'col10',
      headerSortingClasses: 'sortingColumn',
    },
    {
      dataField: 'status',
      text: 'Status',
      classes: 'col5',
      headerSortingClasses: 'sortingColumn',
    },
    {
      dataField: 'insightDescription',
      text: 'Reason for insight',
      classes: 'col20',
      headerSortingClasses: 'sortingColumn',
    },
    {
      dataField: 'followUpNotes',
      text: 'Action log',
      classes: 'col40',
      formatter: notesFormatter,
    },
    {
      ...(showMentalIlLStatus
        ? {
            dataField: 'scale',
            text: 'Ment. ill scale',
            classes: 'col5',
            headerSortingClasses: 'sortingColumn',
          }
        : {}),
    },
    {
      ...(showSupport
        ? {
            dataField: 'support',
            text: 'Subseq. support',
            classes: 'col10',
            headerSortingClasses: 'sortingColumn',
          }
        : {}),
    },
    {
      dataField: 'addAction',
      text: '',
      classes: 'col8',
      formatter: linkFormatter,
      formatExtraData: 'Add action',
    },
    {
      dataField: 'view',
      text: '',
      classes: 'col6',
      formatter: linkFormatter,
      formatExtraData: 'View',
    },
    {
      dataField: 'export',
      text: '',
      classes: 'col6',
      formatter: linkFormatter,
      formatExtraData: 'Export',
    },
  ];

  // Sort Insight by Date
  let insightsSorted = insights.slice().sort((a, b) => {
    let c = moment(new Date(a.when_created));
    let d = moment(new Date(b.when_created));
    return d - c;
  });

  // Mapping data to table Format
  const mappedInsights = DataMapperInsightArchiveTable(insightsSorted);

  // filter data ( hide ignored by default)
  let filterMappedInsights = mappedInsights;

  if (!isChecked) {
    filterMappedInsights = mappedInsights.filter((f) => f.status !== 'Ignored');
  } else {
    filterMappedInsights = mappedInsights;
  }

  // row class for diseable and enable row
  const rowClasses = (row, rowIndex) => {
    return row.status === 'Ignored' ? 'disabledRow' : 'enabledRow';
  };

  // handle to switch state
  const handleChange = (e) => {
    setIsChecked(!isChecked);
    if (isChecked) {
      filterMappedInsights = mappedInsights.filter(
        (f) => f.status !== 'Ignored',
      );
    } else {
      filterMappedInsights = mappedInsights;
    }
  };

  let uKey = 0;

  let emptyFilterMappedInsights;
  if (filterMappedInsights.length == 0) {
    emptyFilterMappedInsights = [
      {
        id: 0,
      },
    ];
  }

  // Rendering //
  // react-bootstrap-table2: https://react-bootstrap-table.github.io/react-bootstrap-table2/
  return (
    <>
      <div>
        <div className="flexHorizWrap" style={{ padding: '0px 19px 0px 19px' }}>
          <div className="flexItem">
            <h1 className="heading1" style={{ margin: '50px 0px 15px 0px' }}>
              Insight archive
            </h1>
          </div>
          <div
            className="flexHorizWrap"
            style={{ alignSelf: 'center', margin: '0px 10px 0px 0px' }}
          >
            <label className="lblSwitch">
              {isChecked ? 'Hide' : 'Show'} ignored
            </label>
            <div>
              <label className="switch">
                <input
                  type="checkbox"
                  onChange={handleChange}
                  checked={isChecked}
                ></input>
                <span className="slider round"></span>
              </label>
            </div>
          </div>
        </div>
        <div style={{ padding: '0px 11px 150px 11px' }}>
          {
            // TODO - custom pagination to design like invision - https://react-bootstrap-table.github.io/react-bootstrap-table2/storybook/index.html?selectedKind=Pagination&selectedStory=Fully%20Custom%20Pagination&full=0&addons=1&stories=1&panelRight=0&addonPanel=storybook%2Factions%2Factions-panel
          }
          <BootstrapTable
            bootstrap4
            keyField="id"
            wrapperClasses="table-responsive"
            id="insightTable"
            bordered={false}
            data={emptyFilterMappedInsights ?? filterMappedInsights}
            columns={columns}
            rowClasses={rowClasses}
            key={uKey++}
            pagination={paginationFactory({
              sizePerPage: 5,
              sizePerPageList: [5, 10, 20, 50],
            })}
          />
        </div>
      </div>
      <InsightsOffCanvas />
    </>
  );
};
